/* eslint-disable no-unused-vars */
import classnames from "classnames";
import { Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import { Modal } from "@JohnLewisPartnership/jl-design-system/dist/components/modal/Modal";
import { PrimaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import Head from "next/head";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/breadcrumb/Breadcrumb";
import EmailFormNode from "../../components/email-form-node/EmailFormNode";
import EscalateEmailFormNode from "../../components/email-form-node/EscalateEmailFormNode";
import Icon from "../../components/icon/Icon";
import analyticsTrigger from "../../utils/analytics/trigger/analyticsTrigger";
import styles from "./index.module.scss";
import { evaluateFlags, flagsClient, getDefinitions } from "@unleash/nextjs";
import FeatureFlagProvider from "../../context/FeatureFlagContext";
import { isFeatureEnabled } from "@/utils/feature-flags";

const contactNumbers = {
  nonElectrical: "03456 049 049",
  electrical: "0330 123 0106",
};

function Phone({ isElectricalFault }) {
  const number = isElectricalFault ? contactNumbers.electrical : contactNumbers.nonElectrical;
  return <a href={`tel:${number.replace(/ /g, "")}`}>{number}</a>;
}
Phone.propTypes = {
  isElectricalFault: PropTypes.bool,
};
Phone.defaultProps = {
  isElectricalFault: false,
};

const emailCustomerServiceHighVolumes = "false"; // TODO: make this a feature flag
const getSubHeading = () =>
  emailCustomerServiceHighVolumes === "true"
    ? "Our Customer Service team are currently experiencing high volumes of emails. Please be assured we will respond as so on as possible."
    : "Our Customer Service team aims to respond within 24 hours.";

export async function getServerSideProps(ctx) {
  const isLiveChatInEmailUsEnabled = await isFeatureEnabled(ctx, "live_chat_in_email_us");
  const isHideFlooringsActive = await isFeatureEnabled(ctx, "hide_floorings_in_email_us");
  const showMIAWChat = await isFeatureEnabled(ctx, "show_miaw_chat");

  return {
    props: {
      isLiveChatInEmailUsEnabled,
      isHideFlooringsActive,
      showMIAWChat,
    },
  };
}

export const metadata = {
  title: `Email us | Customer Services`,
  description: `Have a question? We're here to help with all your queries, from delivery and returns to orders and your online account and your My John Lewis membership.`,
};

function EmailUs({ isLiveChatInEmailUsEnabled, isHideFlooringsActive, showMIAWChat }) {
  const { pathname, query } = useRouter();
  const canonicalUrl = `https://www.johnlewis.com/customer-services/email-us${
    query?.mode === "escalate" ? "?mode=escalate" : ""
  }`;
  const [isEscalated, setIsEscalated] = useState(false);
  const pageName = isEscalated ? "Escalate issue" : "Email us";
  const pageTitle = `${pageName} | Customer Services | John Lewis & Partners`;
  const [caseNumber, setCaseNumber] = useState(null);
  const [email, setEmail] = useState(null);
  const [subject, setSubject] = useState(null);
  const [hasWarnings, setWarnings] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const isElectricalFault = subject === "Faulty_Product_Electrical";
  const source = query.source ? decodeURIComponent(query.source) : undefined;

  useEffect(() => {
    setIsEscalated(query?.mode === "escalate");
  }, [query]);

  useEffect(() => {
    analyticsTrigger("dataStateChange", pathname, {}, "pageload");
  }, []);

  const handleOnSubmit = (propCaseNumber, propEmail, propSubject, propShop, propWarnings) => {
    setCaseNumber(propCaseNumber);
    setEmail(propEmail);
    setSubject(propSubject);
    setWarnings(propWarnings);
    window.scrollTo(0, 0);
  };

  const renderEmailUsFeedback = () => {
    const feedbackFromURL = new URL(
      `https://docs.google.com/forms/d/1PKtNzUBXxEA3f863kq5m_T_1UhiPw3LHJhl-51g1SlE/viewform`,
    );
    caseNumber && feedbackFromURL.searchParams.append("entry.902292776", caseNumber);
    query.t && feedbackFromURL.searchParams.append("entry.1749702081", query.t);
    return (
      <div className={styles.feedbackContainer}>
        <h2>Before you go</h2>
        <p>
          We‘d love to get some feedback about our online email form so we can continuously improve the experience based
          on what our customers need
        </p>
        <PrimaryButton onClick={() => setShowModal(true)}>Leave feedback</PrimaryButton>
        <Modal
          width="large"
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title="Customer Feedback"
          className={styles.feedbackFormModal}
          rootId="page-root"
          footer={false}
        >
          <iframe
            title="feedback-form"
            className={styles.feedbackForm}
            referrerPolicy="origin"
            sandbox="allow-scripts allow-forms allow-same-origin allow-top-navigation"
            name=""
            src={feedbackFromURL}
          />
        </Modal>
      </div>
    );
  };

  return (
    <>
      <FeatureFlagProvider features={{ show_miaw_chat: showMIAWChat }}>
        <Head>
          <title>{pageTitle}</title>
          <link rel="canonical" href={canonicalUrl} />
          <meta property="og:url" content={canonicalUrl} />
          <meta name="robots" content="noindex, follow" />
        </Head>
        <Breadcrumb links={[{ text: "Contact us", href: "/contact-us" }]} className={styles.emailUsBreadcrumb} />
        <div className={classnames(styles["email-us__container"], "util-container")}>
          {!caseNumber ? (
            <>
              <h1 className="util-sectionHeading">{pageName}</h1>
              <div className={styles["email-us__info"]}>
                <p className={styles["email-us__sectionSubheading"]}>
                  {isEscalated
                    ? "Please fill out your personal details, providing your case number and an explanation regarding the issue you're having."
                    : getSubHeading()}
                </p>
              </div>
              {isEscalated ? (
                <EscalateEmailFormNode
                  isLiveChatInEmailUsEnabled={isLiveChatInEmailUsEnabled}
                  isHideFlooringsActive={isHideFlooringsActive}
                  onSubmitted={handleOnSubmit}
                  source={source}
                />
              ) : (
                <EmailFormNode
                  isLiveChatInEmailUsEnabled={isLiveChatInEmailUsEnabled}
                  onSubmitted={handleOnSubmit}
                  source={source}
                />
              )}
            </>
          ) : (
            <>
              <h1 className="util-sectionHeading">Thank you</h1>
              <h2 className={styles.confirmationSubTitle}>
                We&apos;ve got your email. <br />
                {isEscalated ? "Your new case reference number is" : "Your case reference number is"}
                <br />
                <strong data-test="case-number">{caseNumber}</strong>
              </h2>
              <div>
                <div className={styles.emailUsSubmissionSuccessContainer}>
                  <div className={styles.emailUsSubmissionInfoAndIcon}>
                    <Icon name="email" />
                    <span>
                      We&apos;ll aim to get back to you{" "}
                      <strong>
                        {emailCustomerServiceHighVolumes === "true" ? "as soon as possible" : "within 24 hours"}
                      </strong>{" "}
                      at this email address: <strong data-test="email">{email}</strong>
                    </span>
                  </div>
                  {!isEscalated && (
                    <>
                      <div className={styles.dividerWithText}>OR</div>
                      <div className={styles.callUsSubmissionInfoAndIcon}>
                        <Icon name="headset" />
                        <div>
                          <span>
                            If you need to speak to someone now,{" "}
                            <strong>
                              call us on <Phone isElectricalFault={isElectricalFault} />{" "}
                            </strong>{" "}
                            with your case reference number.
                          </span>
                          <div className={styles.openingHours}>
                            <strong>Opening hours:</strong>
                            {isElectricalFault ? (
                              <p>
                                Monday - Friday, 8am - 9pm <br />
                                Saturday, 8am - 8pm <br />
                                Sunday, 10am - 6pm
                              </p>
                            ) : (
                              <p>
                                Monday - Sunday, 8am - 9pm <br />
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {isElectricalFault ? null : (
                    <div className={styles.accountContainer}>
                      <h2>Got an account with us?</h2>
                      <p>
                        You can{" "}
                        <a href={`${process.env.NEXT_PUBLIC_ACCOUNT_DOMAIN}/cases/${caseNumber}`}>
                          <strong>sign in</strong>
                        </a>{" "}
                        to check your <strong>case status</strong>. Please use the same email address you emailed us
                        with.
                      </p>
                    </div>
                  )}
                  {!isEscalated && renderEmailUsFeedback()}
                </div>
                {hasWarnings && (
                  <div className={styles["email-us__warning-message"]}>
                    <br />
                    <Message
                      type="warning"
                      title="Unfortunately your image files have not loaded"
                      body="Our team may ask for the photos to be resent if needed"
                      whiteBackground
                      wideTab
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </FeatureFlagProvider>
    </>
  );
}

EmailUs.metadata = metadata;

export default EmailUs;
